// The renderer host (P0.8) — Architecture §2, §6.1.
//
// "Rendering runs in the render worker via OffscreenCanvas with a same-interface main-thread
// fallback." This is that interface, and the factory that picks a side.
//
// The interface is deliberately small. Everything interesting — the scene, the loop, the stats —
// is shared; the hosts differ only in *where* it runs. Anything that lands in one host and not
// the other is a bug waiting for the browser nobody develops on.

import {
  RENDER_STATS_BYTES,
  type RenderPath,
  type RenderWorkerEvent,
  type RenderWorkerMessage,
} from '../../platform/types/render';
import { RenderLoop } from './render-loop';
import { createSpinningRenderer } from './spinning-renderer';

export interface RendererHost {
  readonly path: RenderPath;
  /** Where frames are reported. Same buffer, same layout, whichever path is running. */
  readonly stats: SharedArrayBuffer;
  /**
   * ⚠️ Why the renderer never started, or null if it did (P4.15).
   *
   * `path` says which side was *attempted*, not which one ran — a distinction with no consequences
   * until 11 September, when a Chrome window with a dead GPU process reported `renderPath: "worker"`
   * beside thirty minutes of zero frames. Both are true and neither is the story.
   *
   * A getter rather than a constructor result because the worker path learns this asynchronously:
   * `new Worker` and `postMessage` both succeed, and the failure arrives a message later.
   */
  readonly startupError: string | null;
  resize(width: number, height: number, pixelRatio: number): void;
  dispose(): void;
}

/**
 * Can this browser hand a canvas to a worker?
 *
 * All of Tier 1 and modern Tier 2 can (Architecture §2). Safari gained OffscreenCanvas in 16.4;
 * anything older takes the fallback, which is PRD §6.3's rule working — "no feature may crash
 * Tier 2; it degrades or hides".
 */
export function supportsOffscreenCanvas(): boolean {
  return (
    typeof OffscreenCanvas !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.transferControlToOffscreen === 'function'
  );
}

/** The flag P0.8's verify means by "toggling paths is one flag". */
export function defaultRenderPath(): RenderPath {
  return supportsOffscreenCanvas() ? 'worker' : 'main-thread';
}

/**
 * The worker path. The canvas is *transferred*, not shared: after this the main thread cannot
 * draw to it or resize it, which is why resize has to be a message.
 *
 * That one-way handover is the point. Once the canvas is gone, nothing on the main thread can
 * contend with the frame loop — R-1's mitigation, made structural rather than promised.
 */
/**
 * Where the render worker's bundle lives — the same stable URL in dev and production, exactly
 * like the worklet's (WORKLET_MODULE_URL).
 *
 * Not `new Worker(new URL('./render-worker.entry.ts', import.meta.url))`, which is the idiomatic
 * Vite spelling and was wrong here for a reason worth remembering. That form makes Vite emit its
 * *own* worker chunk, which meant two consequences at P0.8: Three was bundled twice (175 kB gzip
 * of dead weight shipped, since nothing loaded vite.bundles.ts's copy), and — the part that
 * matters — the bundle guard ran on the copy nobody loaded. A guard pointed at a shadow is not a
 * guard. This URL is the one vite.bundles.ts builds, guards, and emits, and the one the dev
 * middleware serves.
 */
export const RENDER_WORKER_URL = '/render-worker.js';

class WorkerRendererHost implements RendererHost {
  readonly path = 'worker' as const;
  readonly stats = new SharedArrayBuffer(RENDER_STATS_BYTES);
  readonly #worker: Worker;
  #startupError: string | null = null;

  get startupError(): string | null {
    return this.#startupError;
  }

  constructor(
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
    pixelRatio: number,
  ) {
    this.#worker = new Worker(RENDER_WORKER_URL, {
      type: 'module',
      name: 'synapse-render',
    });

    // Listened for before `init` is sent, so a worker that fails instantly cannot beat the
    // listener to the punch.
    this.#worker.addEventListener('message', (event: MessageEvent<RenderWorkerEvent>) => {
      // `RenderWorkerEvent` has one member today, so the discriminant reads as always-true and the
      // linter says so. It is checked anyway: a second event type added later would otherwise be
      // treated as a startup failure in silence, and this listener exists because of a failure that
      // was silent.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (event.data.type === 'startup-failed') {
        this.#startupError = event.data.message;
      }
    });
    // The other half: a worker that fails to *load* — a 404 on the bundle, a syntax error — never
    // runs the code that would post back, so `error` is the only event that fires.
    this.#worker.addEventListener('error', (event: ErrorEvent) => {
      this.#startupError ??= event.message || 'the render worker failed to load';
    });

    const offscreen = canvas.transferControlToOffscreen();
    const message: RenderWorkerMessage = {
      type: 'init',
      canvas: offscreen,
      stats: this.stats,
      width,
      height,
      pixelRatio,
    };
    this.#worker.postMessage(message, [offscreen]);
  }

  resize(width: number, height: number, pixelRatio: number): void {
    const message: RenderWorkerMessage = { type: 'resize', width, height, pixelRatio };
    this.#worker.postMessage(message);
  }

  dispose(): void {
    const message: RenderWorkerMessage = { type: 'dispose' };
    this.#worker.postMessage(message);
    this.#worker.terminate();
  }
}

/** The fallback. Same renderer, same loop, same stats — just on this thread. */
class MainThreadRendererHost implements RendererHost {
  readonly path = 'main-thread' as const;
  readonly stats = new SharedArrayBuffer(RENDER_STATS_BYTES);
  readonly #renderer: ReturnType<typeof createSpinningRenderer> | null = null;
  readonly #loop: RenderLoop | null = null;
  readonly startupError: string | null = null;

  constructor(
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
    pixelRatio: number,
  ) {
    // Caught, not thrown, so both paths fail the same way — this file's header asks for exactly
    // that: "anything that lands in one host and not the other is a bug waiting for the browser
    // nobody develops on". A GPU that cannot give a context is not a Tier-2 browser question; the
    // 11 September failure was Chrome on the reference-class laptop.
    try {
      this.#renderer = createSpinningRenderer({ canvas, width, height, pixelRatio });
      this.#loop = new RenderLoop(this.#renderer, this.stats);
      this.#loop.start();
    } catch (cause) {
      this.startupError = cause instanceof Error ? cause.message : String(cause);
    }
  }

  resize(width: number, height: number, pixelRatio: number): void {
    this.#renderer?.resize(width, height, pixelRatio);
  }

  dispose(): void {
    this.#loop?.stop();
    this.#renderer?.dispose();
  }
}

export function createRendererHost(
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  pixelRatio: number,
  path: RenderPath = defaultRenderPath(),
): RendererHost {
  return path === 'worker'
    ? new WorkerRendererHost(canvas, width, height, pixelRatio)
    : new MainThreadRendererHost(canvas, width, height, pixelRatio);
}
